// MonitorCard — T053

import { Link } from 'react-router-dom';
import StatusBadge from './StatusBadge';
import type { Monitor } from '../services/api';

interface MonitorCardProps {
  monitor: Monitor;
  isAdmin: boolean;
  onToggleMute?: (id: string, muted: boolean) => void;
}

export default function MonitorCard({ monitor, isAdmin, onToggleMute }: MonitorCardProps) {
  const timeSince = monitor.lastPollAt
    ? formatTimeAgo(new Date(monitor.lastPollAt))
    : 'Never';

  return (
    <Link
      to={`/monitors/${monitor.id}`}
      className="block bg-surface rounded-lg p-4 hover:bg-surface-hover transition-colors border border-border"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-text-primary font-semibold truncate mr-2">{monitor.name}</h3>
        <StatusBadge state={monitor.state} />
      </div>
      <p className="text-text-secondary text-small truncate mb-2">{monitor.url}</p>
      <div className="flex flex-wrap gap-1 mb-2">
        {monitor.hostingProvider && (
          <span className="bg-bg-primary text-text-secondary text-xs px-2 py-0.5 rounded">{monitor.hostingProvider}</span>
        )}
        {monitor.providerAccount && (
          <span className="bg-bg-primary text-text-secondary text-xs px-2 py-0.5 rounded">{monitor.providerAccount}</span>
        )}
        {monitor.client && (
          <span className="bg-bg-primary text-text-secondary text-xs px-2 py-0.5 rounded">{monitor.client}</span>
        )}
      </div>
      <div className="flex items-center justify-between text-text-muted text-xs">
        <span>Last checked: {timeSince}</span>
        <div className="flex items-center gap-2">
          {monitor.muted && <span className="text-status-degraded">🔇 Muted</span>}
          {isAdmin && onToggleMute && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleMute(monitor.id, !monitor.muted);
              }}
              className="text-text-secondary hover:text-text-primary px-2 py-1 rounded hover:bg-bg-primary min-h-[44px] min-w-[44px] flex items-center justify-center"
              title={monitor.muted ? 'Unmute' : 'Mute'}
            >
              {monitor.muted ? '🔊' : '🔇'}
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
